export default {
  // Page titles
  title: 'Units',
  unit_detail: 'Unit Details',
  edit_unit: 'Edit Unit',
  form: {
    title_new: 'Add unit',
  },

  // EP-54: i18next v25 plural forms for count-with-noun renders.
  // Use via `t('units.units_count', { count: n })` — auto-selects _one or _other.
  units_count_one: '{{count}} unit',
  units_count_other: '{{count}} units',
  vacant_count_one: '{{count}} vacant',
  vacant_count_other: '{{count}} vacant',

  // Descriptions
  description: 'Manage rental units',
  edit_description: 'Update unit details',

  // Labels
  labels: {
    unit_name: 'Unit Name',
    unit_number: 'Unit Number',
    monthly_rent: 'Monthly Rent',
    monthly_rent_thb: 'Monthly Rent (฿)',
    room_type: 'Room Type',
    room_type_move_hint: 'Move this unit to a different room type within the same property.',
    property: 'Property',
    status: 'Status',
    occupancy: 'Occupancy',
    current_tenant: 'Current Tenant',
    meters: 'Meters',
    units: 'Units',
    override_rates: 'Override property rates for this unit',
    uses_property_default: 'Uses property default rates',
    custom_rates: 'Custom Rates',
    utility_rates_override: 'Utility Rates Override',
    property_default: 'Property Default',
    area_sqm: 'Area (sqm)',
    share_ratio: 'Share Ratio',
    condominium_fields: 'Condominium Fields',
    condominium_fields_description: 'Required for common area fee calculations (per sqm or share ratio methods)',
  },

  // Placeholders
  placeholders: {
    unit_name: 'e.g., 101, A1',
    monthly_rent: '5000',
    price_example: '5000',
  },

  // Status
  status: {
    vacant: 'Vacant',
    occupied: 'Occupied',
    // EP-136 T136-020 — a unit HELD by a paid/billed booking fee (draft
    // RentalLease + current_booking), distinct from both vacant and occupied.
    reserved: 'Reserved',
  },

  // Room type section
  room_type: {
    title: 'Room Type',
    detail: 'Room Type Details',
    edit: 'Edit Room Type',
    edit_description: 'Update room type details',
    name: 'Name',
    name_placeholder: 'e.g., Studio, 1BR, 2BR',
    floor: 'Floor',
    floor_placeholder: 'e.g., 1, G, B1',
    not_found: 'Room type not found',
  },

  // Actions
  actions: {
    add_unit: 'Add Unit',
    add_meter: 'Add Meter',
    add_meter_to_unit: 'Add Meter to Unit {{name}}',
    view_lease: 'View Lease',
    meter: 'Meter',
    // EP-134 §7 (T134-007) — Transfer Ownership lives on the unit-detail
    // page's overflow menu (admin+ only; gated on `condominium_enabled`).
    transfer_ownership: 'Transfer Ownership',
    // EP-136 T136-022 — gated on `unit.bookable === true`, never on
    // `unit.status === 'vacant'` (see UnitDetailPage.tsx).
    book: 'Book',
  },

  // EP-136 T136-022/T136-023 — Book action, the Book modal form, and the
  // reservation card.
  booking: {
    modal_title: 'Book Unit',
    card_title: 'Reservation',
    reserved_by: 'Reserved By',
    no_customer: 'No customer on file',
    expires_at: 'Reservation Expires',
    days_left_one: '{{count}} day left',
    days_left_other: '{{count}} days left',
    invoice: 'Booking Fee Invoice',
    invoice_paid: 'Paid',
    invoice_unpaid: 'Unpaid',
    view_draft_lease: 'View Draft Lease',

    // T136-023 — the Book modal form fields. The booking MODE is resolved
    // server-side and shown read-only here (never a form control) — see
    // BookingModal.tsx's file banner.
    form: {
      customer_label: 'Customer',
      customer_placeholder: 'Search customers…',
      started_at_label: 'Start Date',
      started_at_hint: "The tenant's intended move-in date for the draft lease this booking creates.",
      mode_label: 'Booking Fee Treatment',
      mode_hint: 'Set on the property, not here.',
      mode_hint_link: 'Manage in Booking settings',
      amount_label: 'Booking Amount',
      reservation_expires_at_label: 'Reservation Expires',
      reservation_expires_at_hint: 'The unit stays reserved until this date. Defaults to the end of the current month.',
    },

    messages: {
      create_success: 'Unit booked successfully',
    },
  },

  // Empty states
  empty: {
    no_units: 'No units',
    no_units_description: 'Add units to this room type.',
    not_found: 'Unit not found',
  },

  // Setup guide (room type detail - units section)
  setup_guide: {
    title: 'Add Units to This Room Type',
    description:
      'Units are individual rooms that tenants can rent. Add units with a name and monthly rent price.',
    step_1_title: 'Create a unit',
    step_1_description: 'Give it a name (e.g., 101, A1) and set the monthly rent.',
    step_2_title: 'Add meters',
    step_2_description: 'Set up water and electricity meters for each unit to track usage.',
    import: 'Import',
    import_off_scope_warning: 'Found {{count}} {{entity}} rows. Import them from the {{Entity}} page.',
    download_sample: 'Download sample',
    seed_sample: 'Seed sample data',
    seed_sample_confirm: 'This will create: 1 unit in this property. Continue?',
    seed_sample_success: 'Sample unit created successfully.',
    seed_sample_noop: 'You already have units. Sample data was not added.',
  },

  // Messages
  messages: {
    delete_confirm: 'Delete unit "{{name}}"?',
    delete_room_type_confirm: 'Delete "{{name}}"? This will also delete all units.',
    delete_unit_cannot_undo: 'Delete unit "{{name}}"? This action cannot be undone.',
    delete_blocked_occupied: 'Cannot delete an occupied unit',
    created: 'Unit created successfully',
    updated: 'Unit updated successfully',
    deleted: 'Unit deleted successfully',
  },

  // Validation
  validation: {
    unit_name_required: 'Unit name is required',
    price_required: 'Price is required',
    name_required: 'Name is required',
    must_be_positive: 'Must be positive',
  },

  errors: {
    validation_failed: 'Validation failed. Please check the form and try again.',

    // T136-023 — the 6 typed 422 codes UnitBookingService raises
    // (api/app/services/unit_booking_service.rb), translated via
    // useApiErrorTranslator('units') in BookingModal.
    booking_not_available_for_condominium_account: 'Booking is not available on a condominium account.',
    unit_occupied: 'This unit is already occupied or has a lease in progress.',
    unit_already_reserved: 'This unit is already reserved.',
    amount_required: 'Enter a booking amount.',
    amount_invalid: 'The booking amount must be greater than zero.',
    amount_exceeds_deposit: 'The booking amount cannot exceed the lease deposit.',

    fields: {
      name: 'Name',
      floor: 'Floor',
      room_type_id: 'Room type',
      monthly_price: 'Monthly price',
      deposit_amount: 'Deposit amount',
    },

    codes: {
      blank: "can't be blank",
      taken: 'is already taken',
      invalid: 'is invalid',
      too_short: 'is too short',
      too_long: 'is too long',
      greater_than: 'must be greater than the limit',
      greater_than_or_equal_to: 'must be greater than or equal to the limit',
      less_than: 'must be less than the limit',
      less_than_or_equal_to: 'must be less than or equal to the limit',
      not_a_number: 'is not a number',
      inclusion: 'is not included in the list',
    },
  },
}
